import React, { useState, useEffect, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MapPin, Loader2 } from 'lucide-react';

interface AddressSuggestion {
  place_name: string;
  center: [number, number];
  properties: {
    address?: string;
    short_code?: string;
  };
  context?: Array<{
    id: string;
    text: string;
    short_code?: string;
  }>;
}

interface AddressComponents {
  street?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
  fullAddress?: string;
}

interface AddressAutocompleteProps {
  label?: string;
  placeholder?: string;
  required?: boolean;
  value?: AddressComponents;
  onChange?: (address: AddressComponents) => void;
  error?: string;
  disabled?: boolean;
  className?: string;
  countryCode?: string;
}

const MAPBOX_API_KEY = 'pk.eyJ1IjoibXNlcnJhbHRhIiwiYSI6ImNtNGoxeHFpbTA5NjYyanB0MzZtcXgzNHkifQ.bVlwWZU58veskn44qwXn9g';

export function AddressAutocomplete({
  label = "Address",
  placeholder = "Enter address...",
  required = false,
  value,
  onChange,
  error,
  disabled = false,
  className = "",
  countryCode = "US"
}: AddressAutocompleteProps) {
  const [query, setQuery] = useState(value?.fullAddress || '');
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (value?.fullAddress && value.fullAddress !== query) {
      setQuery(value.fullAddress);
    }
  }, [value?.fullAddress]);

  useEffect(() => {
    setSuggestions([]);
    setShowSuggestions(false);
  }, [countryCode]);

  const searchAddresses = async (searchQuery: string) => {
    if (!searchQuery.trim() || searchQuery.length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setIsLoading(true);
    try {
      const countryParam = countryCode ? `&country=${encodeURIComponent(countryCode)}` : '';
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(searchQuery)}.json?access_token=${MAPBOX_API_KEY}${countryParam}&types=address,poi&limit=5`
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch suggestions');
      }
      
      const data = await response.json();
      setSuggestions(data.features || []);
      setShowSuggestions(true);
      setSelectedIndex(-1);
    } catch (error) {
      console.error('Error fetching address suggestions:', error);
      setSuggestions([]);
      setShowSuggestions(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newQuery = e.target.value;
    setQuery(newQuery);
    
    // Clear existing debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    
    // Debounce the search
    debounceRef.current = setTimeout(() => {
      searchAddresses(newQuery);
    }, 300);
  };

  const parseAddress = (suggestion: AddressSuggestion): AddressComponents => {
    const { place_name, context = [] } = suggestion;
    
    let street = '';
    let city = '';
    let state = '';
    let zipCode = '';
    let country = '';

    const addressParts = place_name.split(', ');

    if (addressParts.length > 0) {
      street = addressParts[0];
    }

    for (const ctx of context) {
      if (ctx.id.includes('place')) {
        city = ctx.text;
      } else if (ctx.id.includes('region')) {
        const stateCode = ctx.short_code || ctx.text;
        state = stateCode.includes('-') ? stateCode.split('-')[1] : stateCode;
      } else if (ctx.id.includes('postcode')) {
        zipCode = ctx.text;
      } else if (ctx.id.includes('country')) {
        country = ctx.text;
      }
    }

    if (!city && addressParts.length > 1) {
      city = addressParts[1];
    }

    if (!state && addressParts.length > 2) {
      const stateZip = addressParts[2].split(' ');
      if (stateZip.length > 0) {
        state = stateZip[0];
      }
      if (stateZip.length > 1) {
        zipCode = stateZip[1];
      }
    }

    if (!country && addressParts.length > 0) {
      country = addressParts[addressParts.length - 1];
    }

    return {
      street: street || '',
      city: city || '',
      state: state ? state.toUpperCase() : '',
      zipCode: zipCode || '',
      country: country || '',
      fullAddress: place_name || ''
    };
  };

  const handleSuggestionClick = (suggestion: AddressSuggestion) => {
    const addressComponents = parseAddress(suggestion);
    setQuery(addressComponents.fullAddress);
    setShowSuggestions(false);
    
    if (onChange) {
      onChange(addressComponents);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions || suggestions.length === 0) return;
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0) {
          handleSuggestionClick(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
    }
  };

  const handleBlur = () => {
    // Delay hiding suggestions to allow for clicks
    setTimeout(() => {
      setShowSuggestions(false);
      setSelectedIndex(-1);
    }, 200);
  };

  return (
    <div className={`relative ${className}`}>
      {label && (
        <Label htmlFor="address-input">
          {label} {required && <span className="text-red-500">*</span>}
        </Label>
      )}
      <div className="relative">
        <Input
          ref={inputRef}
          id="address-input"
          type="text"
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          placeholder={placeholder}
          disabled={disabled}
          className={error ? 'border-red-500' : ''}
        />
        <div className="absolute inset-y-0 right-0 flex items-center pr-3">
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
          ) : (
            <MapPin className="h-4 w-4 text-gray-400" />
          )}
        </div>
      </div>
      
      {error && (
        <p className="text-sm text-red-600 mt-1">{error}</p>
      )}
      
      {showSuggestions && suggestions.length > 0 && (
        <div
          ref={suggestionsRef}
          className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto"
        >
          {suggestions.map((suggestion, index) => (
            <div
              key={index}
              className={`p-3 cursor-pointer hover:bg-gray-50 ${
                index === selectedIndex ? 'bg-blue-50' : ''
              }`}
              onClick={() => handleSuggestionClick(suggestion)}
            >
              <div className="flex items-start">
                <MapPin className="h-4 w-4 text-gray-400 mt-0.5 mr-2 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {suggestion.place_name}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}